// Shared helpers for the admin console pages.

export const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => toDate(value)?.toLocaleDateString() || 'Unknown';

export const formatMoney = (amount) =>
  `R${(Number(amount) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const isWithinDays = (value, days) => {
  const date = toDate(value);
  return Boolean(date) && Date.now() - date.getTime() <= days * 24 * 60 * 60 * 1000;
};

export const userName = (user) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.displayName || user?.email || 'Unknown user';

// What a business still needs before its setup is complete.
// `owner` is the owner's entry from getAdminConsoleData (may be undefined).
export const setupChecklist = (business, owner) => [
  { label: 'Profile image', done: Boolean(business.profileImageUrl) },
  { label: 'Description', done: Boolean(business.description?.trim()) },
  { label: 'Category', done: Boolean(business.category) },
  { label: 'Address', done: Boolean(business.address?.trim()) },
  { label: 'Phone', done: Boolean(business.phone?.trim()) },
  { label: 'Email', done: Boolean(business.email?.trim()) },
  { label: 'Business hours', done: Boolean(business.businessHours?.trim()) },
  { label: 'At least one service', done: (business.services?.length || 0) > 0 },
  { label: 'Company documents', done: (business.companyDocumentUrls?.length || 0) > 0 },
  { label: 'Banking / payouts', done: Boolean(owner?.hasPaystack || owner?.hasBanking) },
];

export const setupProgress = (business, owner) => {
  const items = setupChecklist(business, owner);
  const done = items.filter(i => i.done).length;
  return { items, done, total: items.length, complete: done === items.length };
};

export const isPaid = (payment) => ['completed', 'paid', 'success', 'released'].includes(payment.status);

// CSV export. Cells starting with = + - @ are prefixed so spreadsheets don't run them as formulas.
const csvCell = (value) => {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const isoDate = (value) => toDate(value)?.toISOString().slice(0, 19).replace('T', ' ') || '';

// columns: [{ header, value: row => any }]
export const downloadCsv = (name, columns, rows) => {
  const lines = [
    columns.map(c => csvCell(c.header)).join(','),
    ...rows.map(row => columns.map(c => csvCell(c.value(row))).join(',')),
  ];
  // BOM so Excel opens UTF-8 names correctly.
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `snapconx-${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const exportUsersCsv = (users, businesses) => {
  const owned = (id) => businesses.filter(b => b.ownerId === id).map(b => b.businessName).join('; ');
  downloadCsv('users', [
    { header: 'User ID', value: u => u.id },
    { header: 'First name', value: u => u.firstName },
    { header: 'Last name', value: u => u.lastName },
    { header: 'Email', value: u => u.email },
    { header: 'Phone', value: u => u.phone },
    { header: 'Admin', value: u => (u.isAdmin ? 'Yes' : 'No') },
    { header: 'Status', value: u => (u.isSuspended ? 'Deactivated' : 'Active') },
    { header: 'Businesses', value: u => owned(u.id) },
    { header: 'Payouts', value: u => (u.hasPaystack ? 'Paystack ready' : u.hasBanking ? 'Bank details only' : 'Not set up') },
    { header: 'Joined', value: u => isoDate(u.createdAt) },
  ], users);
};

export const exportBusinessesCsv = (businesses, usersById) => {
  downloadCsv('businesses', [
    { header: 'Business ID', value: b => b.id },
    { header: 'Name', value: b => b.businessName },
    { header: 'Category', value: b => b.category },
    { header: 'Email', value: b => b.email },
    { header: 'Phone', value: b => b.phone },
    { header: 'Address', value: b => b.address },
    { header: 'Owner', value: b => (usersById[b.ownerId] ? userName(usersById[b.ownerId]) : '') },
    { header: 'Owner email', value: b => usersById[b.ownerId]?.email },
    { header: 'Owner ID', value: b => b.ownerId },
    { header: 'Verified', value: b => (b.isVerified ? 'Yes' : 'No') },
    { header: 'Archived', value: b => (b.isArchived ? 'Yes' : 'No') },
    { header: 'Setup', value: b => { const p = setupProgress(b, usersById[b.ownerId]); return `${p.done}/${p.total}`; } },
    { header: 'Missing', value: b => setupChecklist(b, usersById[b.ownerId]).filter(i => !i.done).map(i => i.label).join('; ') },
    { header: 'Services', value: b => b.services?.length || 0 },
    { header: 'Created', value: b => isoDate(b.createdAt) },
  ], businesses);
};

export const exportPaymentsCsv = (payments, businessesById, usersById) => {
  downloadCsv('transactions', [
    { header: 'Date', value: p => isoDate(p.createdAt) },
    { header: 'Type', value: p => (p.kind === 'order' ? 'Product order' : 'Booking') },
    { header: 'Business', value: p => businessesById[p.businessId]?.businessName || p.businessName },
    { header: 'Business ID', value: p => p.businessId },
    { header: 'Customer', value: p => (usersById[p.userId] ? userName(usersById[p.userId]) : '') },
    { header: 'Customer email', value: p => usersById[p.userId]?.email || p.userEmail },
    { header: 'Amount (ZAR)', value: p => p.amount.toFixed(2) },
    { header: 'Platform fee (ZAR)', value: p => p.platformFee.toFixed(2) },
    { header: 'Business payout (ZAR)', value: p => p.businessPayout.toFixed(2) },
    { header: 'Status', value: p => p.status },
    { header: 'Reference', value: p => p.reference },
    { header: 'Record ID', value: p => p.id },
  ], payments);
};
